import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import i18n from '@/i18n'
import { api } from '@/lib/api'
import type { Label } from '@/lib/types'

export function useLabels() {
  return useQuery({
    queryKey: ['labels'],
    queryFn: () => api<{ items: Label[]; total: number }>('/api/labels').then((r) => r.items),
  })
}

export function useCreateLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; color?: string; parentId?: string | null }) =>
      api<Label>('/api/labels', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['labels'] })
      toast.success(i18n.t('toast.labelCreated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useUpdateLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string
      name?: string
      color?: string
      parentId?: string | null
      sortOrder?: number
    }) => api<Label>(`/api/labels/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['labels'] })
      toast.success(i18n.t('toast.labelUpdated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useDeleteLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/labels/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['labels'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
      toast.success(i18n.t('toast.labelDeleted'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}
